import * as _ from 'lodash';
import { useMemo } from 'react';
import { IDonorDeclines } from '@nkr/models/index';
import { AgGridReact } from 'ag-grid-react';
import { Glyphicon, Label, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
    IHeaderParams, GridReadyEvent, ValueGetterParams, FirstDataRenderedEvent, ICellRendererParams, SortChangedEvent, GridSizeChangedEvent, ColDef,
    CellClassParams, RowDataChangedEvent
} from 'ag-grid-community';
import { GridWrapper } from './styles';
import { Utility } from '@nkr/services/utility';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import AlleleMatch from '@nkr/components/allele-match';
import SingleEpletMmRisk from '@nkr/views/swap-management/single-eplet-mm-risk';

interface IProps {
    data: IDonorDeclines[],
    centerId: number,
    onGridReady: (grid: GridReadyEvent) => void
}

const DonorDeclinesGrid = (props: IProps) => {
    const onGridReady = (e: GridReadyEvent) => {
        e.api.sizeColumnsToFit();
        props.onGridReady(e);
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onSortChanged = (e: SortChangedEvent) => {
        e.api.refreshCells();
    };

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        e.api.sizeColumnsToFit();
    }

    const renderPositiveNegative = (value: string, inverse: boolean = false) => {
        if (value == 'POS') {
            return <Glyphicon glyph='plus-sign' style={{ color: !inverse ? '#c00' : null }} />;
        }

        return <Glyphicon glyph='minus-sign' style={{ color: !inverse ? '#0c0' : null }} />;;
    }

    const renderTooltip = (text) => (
        <Tooltip id="tooltip">{text}</Tooltip>
    );

    const columns: ColDef[] = [
        {
            colId: 'rownumber',
            headerName: 'R#',
            width: 45,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}><strong>R#</strong></SortableHeaderComponent>,
            valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1
        },
        {
            colId: 'donor-center',
            headerName: 'Donor Center',
            field: 'centerAlias',
            width: 170,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}><strong>Donor Center</strong></SortableHeaderComponent>
        },
        {
            colId: 'donor-alias',
            headerName: 'Donor Alias',
            field: 'donorAlias',
            width: 170,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}><strong>Donor Alias</strong></SortableHeaderComponent>
        },
        {
            colId: 'abo',
            headerName: 'ABO',
            field: 'abo',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}><strong>ABO</strong></SortableHeaderComponent>,
            width: 70
        },
        {
            colId: 'age',
            headerName: 'Age',
            field: 'age',
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}><strong>Age</strong></SortableHeaderComponent>
        },
        {
            colId: 'wt',
            headerName: 'Wt',
            field: 'weight',
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}><strong>Wt</strong></SortableHeaderComponent>
        },
        {
            colId: 'lat',
            headerName: 'Lat',
            field: 'laterality',
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}><strong>Laterality</strong></SortableHeaderComponent>
        },
        {
            colId: 'cc',
            headerName: 'CC',
            field: 'creatinineClearance',
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}><strong>CC</strong></SortableHeaderComponent>
        },
        {
            colId: 'ebv',
            headerName: 'EBV',
            field: 'ebv',
            width: 90,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} title='EBV'><strong>E</strong></SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IDonorDeclines;

                return renderPositiveNegative(data.ebv, data.isPairedDonor);
            },
        },
        {
            colId: 'cmv',
            headerName: 'CMV',
            field: 'cmv',
            width: 90,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} title='CMV'><strong>C</strong></SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IDonorDeclines;

                return renderPositiveNegative(data.cmv, data.isPairedDonor);
            },
        },
        {
            colId: 'hla',
            headerName: 'HLA',
            field: 'totalHlaPoints',
            hide: true,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}><strong>HLA</strong></SortableHeaderComponent>,
        },
        {
            colId: 'emm',
            headerName: 'Eplet MM',
            field: 'singleEpletMmRisk.sortValue',
            width: 90,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}><strong>Eplet MM</strong></SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IDonorDeclines;
                return (
                    <SingleEpletMmRisk data={data.singleEpletMmRisk} centerId={props.centerId} isAdmin={false} placement='bottom' />
                )
            },
            cellStyle: (params: CellClassParams) => {
                return {
                    textAlign: 'center'
                };
            },
        },
        {
            colId: 'allele-match',
            headerName: 'Allele Match',
            field: 'bmtMm',
            width: 90,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}><strong>Allele M</strong></SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => <AlleleMatch bmtMm={params.value} displayAsInline={false} />,
        },
        {
            colId: 'outcome',
            headerName: 'Outcome',
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}><strong>Outcome</strong></SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                if (params.data.isAccepted == true) {
                    return <Label bsStyle='success' style={{ fontSize: '90%' }}>Accepted</Label>;
                }

                if (params.data.isAccepted == false) {
                    return <Label bsStyle='danger' style={{ fontSize: '90%' }}>Declined</Label>;
                }

                return <Label>Pending</Label>;
            },
            valueGetter: (params: ValueGetterParams) => {
                if (params.data.isAccepted == true) {
                    return 'Accepted';
                }

                if (params.data.isAccepted == false) {
                    return 'Declined';
                }

                return 'Pending';
            },
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}><strong>Activity Date</strong></SortableHeaderComponent>,
            headerName: 'Activity Date',
            colId: 'decisionDateTime',
            field: 'decisionDateTime',
            comparator: Utility.dateComparator,
            sortable: true
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}><strong>Outcome Reason</strong></SortableHeaderComponent>,
            headerName: 'Outcome Reason',
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IDonorDeclines;
                let comments = data.hardBlockReason ?? '';
                let truncatedReason = comments.length > 20 ? comments.substring(0, 20) + '...' : comments;

                if (comments) {
                    return (
                        <OverlayTrigger
                            trigger='hover'
                            placement={'left'}
                            rootClose
                            overlay={renderTooltip(comments)}
                        >
                            <span style={{ cursor: 'pointer' }}>{truncatedReason}</span>
                        </OverlayTrigger>
                    );
                }
                return 'N/A';
            },
            width: 250,
            valueGetter: (params: ValueGetterParams) => {
                if (params.data.hardBlockReason == null || params.data.hardBlockReason == '' || params.data.hardBlockReason == undefined) {
                    return 'N/A';
                }

                return params.data.hardBlockReason;
            },
            sortable: true
        }
    ];

    const columnDefs = useMemo(() => columns, []);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <>
            <GridWrapper id='grid-wrapper' className='ag-theme-bootstrap' style={{ height: '45vh', width: '99.99%' }}>
                <AgGridReact
                    defaultColDef={{
                        cellClass: 'list-cell',
                        headerClass: 'list-header',
                        sortable: true,
                        resizable: false
                    }}
                    gridOptions={{ multiSortKey: 'ctrl' }}
                    columnDefs={columnDefs}
                    rowData={rowData}
                    enableCellTextSelection={true}
                    enableRangeSelection={true}
                    suppressContextMenu={true}
                    suppressCellSelection={true}
                    noRowsOverlayComponentFramework={() => <div>No activity data found...</div>}
                    domLayout={props.data.length == 0 ? 'autoHeight' : null}
                    onGridReady={onGridReady}
                    onGridSizeChanged={onGridSizeChanged}
                    onFirstDataRendered={onFirstDataRendered}
                    onRowDataChanged={onRowDataChanged}
                    onSortChanged={onSortChanged}
                />
            </GridWrapper>
        </>
    );
}


export default DonorDeclinesGrid;
